import React, { useState, useEffect } from "react";
import {
  Table,
  Card,
  Button,
  Space,
  Tag,
  Input,
  Select,
  Row,
  Col,
  Tooltip,
  Popconfirm,
  message,
  Badge,
  Modal,
  Form,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  SearchOutlined,
  ReloadOutlined,
  ToolOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";
import { getAllMachines, deleteMachine, updateMachineStatus } from "../../api/machineApi";
import PageTitle from "../page-header/PageHeader";

const { Option } = Select;

const MACHINE_TYPES = [
  { value: "corrugation", label: "Corrugation" },
  { value: "die_cutting", label: "Die Cutting (Casting)" },
  { value: "rotary", label: "Rotary" },
  { value: "slotter", label: "Slotter" },
  { value: "offset", label: "Offset Printing" },
  { value: "flexo", label: "Flexo Printing" },
  { value: "pasting", label: "Pasting" },
  { value: "sheet_cutter", label: "Sheet Cutter" },
  { value: "stitching", label: "Stitching" },
  { value: "glueing", label: "Glueing" },
  { value: "lamination", label: "Lamination" },
];

const STATUS_COLORS = {
  operational: "green",
  maintenance: "orange",
  breakdown: "red",
};

const MachineList = () => {
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    type: "",
    status: "",
    search: "",
  });
  const [statusModal, setStatusModal] = useState({ visible: false, machine: null });
  const [statusForm] = Form.useForm();
  const navigate = useNavigate();

  useEffect(() => {
    fetchMachines();
  }, [filters.type, filters.status]);

  const fetchMachines = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.type) params.type = filters.type;
      if (filters.status) params.status = filters.status;
      
      const data = await getAllMachines(params);
      const machineList = Array.isArray(data) ? data : data?.data || [];
      setMachines(machineList);
    } catch (error) {
      message.error("Failed to fetch machines");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteMachine(id);
      message.success("Machine deactivated successfully");
      fetchMachines();
    } catch (error) {
      message.error("Failed to deactivate machine");
    }
  };

  const handleStatusChange = async () => {
    try {
      const values = await statusForm.validateFields();
      await updateMachineStatus(statusModal.machine.id, values.status, values.reason);
      message.success("Machine status updated");
      setStatusModal({ visible: false, machine: null });
      statusForm.resetFields();
      fetchMachines();
    } catch (error) {
      message.error("Failed to update status");
    }
  };

  const filteredMachines = machines.filter((m) => {
    if (!filters.search) return true;
    const search = filters.search.toLowerCase();
    return (
      m.name?.toLowerCase().includes(search) ||
      m.code?.toLowerCase().includes(search) ||
      m.type?.toLowerCase().includes(search)
    );
  });

  const columns = [
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      sorter: (a, b) => (a.code || "").localeCompare(b.code || ""),
      render: (code, record) => (
        <Link to={`/machine/${record.id}`}>
          <strong>{code}</strong>
        </Link>
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (type) => {
        const typeObj = MACHINE_TYPES.find((t) => t.value === type);
        return <Tag color="blue">{typeObj?.label || type}</Tag>;
      },
    },
    {
      title: "Subtype",
      dataIndex: "subtype",
      key: "subtype",
      render: (subtype) =>
        subtype ? (
          <Tag color="geekblue">
            {subtype.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
          </Tag>
        ) : (
          "-"
        ),
    },
    {
      title: "Dimensions",
      key: "dimensions",
      render: (_, record) => {
        if (record.width && record.height) {
          return `${record.width}" × ${record.height}"`;
        } else if (record.width) {
          return `${record.width}" (Width)`;
        }
        return "-";
      },
    },
    {
      title: "Speed",
      key: "speed",
      render: (_, record) => {
        if (record.speed_sheets_per_hour) {
          return `${record.speed_sheets_per_hour} sheets/hr`;
        } else if (record.speed_meters_per_min) {
          return `${record.speed_meters_per_min} m/min`;
        }
        return "-";
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Badge
          status={status === "operational" ? "success" : status === "maintenance" ? "warning" : "error"}
          text={<span style={{ textTransform: "capitalize" }}>{status}</span>}
        />
      ),
    },
    {
      title: "Inhouse / Outsource",
      key: "is_inhouse",
      render: (_, record) => {
        if (record.is_inhouse) {
          return (
            <Tooltip title={record.location || "No location specified"}>
              <Tag color="green">Inhouse</Tag>
            </Tooltip>
          );
        } else {
          return (
            <Tooltip title={record.vendor?.name || "No vendor linked"}>
              <Tag color="orange">
                Outsource {record.vendor?.name ? `(${record.vendor.name})` : ""}
              </Tag>
            </Tooltip>
          );
        }
      },
    },
    {
      title: "Active",
      dataIndex: "is_active",
      key: "is_active",
      render: (active) => (
        <Tag color={active ? "green" : "red"}>{active ? "Active" : "Inactive"}</Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/machine/${record.id}`)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => navigate(`/machine/${record.id}/edit`)}
            />
          </Tooltip>
          <Tooltip title="Change Status">
            <Button
              size="small"
              icon={<SettingOutlined />}
              onClick={() => {
                setStatusModal({ visible: true, machine: record });
                statusForm.setFieldsValue({ status: record.status });
              }}
            />
          </Tooltip>
          <Tooltip title="Deactivate">
            <Popconfirm
              title="Deactivate this machine?"
              description="This will mark the machine as inactive."
              onConfirm={() => handleDelete(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageTitle title="Back" subtitle="Machine Management" />
      
      <Card>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="Search machines..."
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Filter by Type"
              style={{ width: "100%" }}
              value={filters.type || undefined}
              onChange={(val) => setFilters({ ...filters, type: val })}
              allowClear
            >
              {MACHINE_TYPES.map((t) => (
                <Option key={t.value} value={t.value}>
                  {t.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Filter by Status"
              style={{ width: "100%" }}
              value={filters.status || undefined}
              onChange={(val) => setFilters({ ...filters, status: val })}
              allowClear
            >
              <Option value="operational">Operational</Option>
              <Option value="maintenance">Maintenance</Option>
              <Option value="breakdown">Breakdown</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={fetchMachines}>
                Refresh
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate("/machine/new")}
              >
                Add Machine
              </Button>
            </Space>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={filteredMachines}
          rowKey="id"
          loading={loading}
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} machines`,
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Status Change Modal */}
      <Modal
        title={`Change Status - ${statusModal.machine?.name}`}
        open={statusModal.visible}
        onOk={handleStatusChange}
        onCancel={() => {
          setStatusModal({ visible: false, machine: null });
          statusForm.resetFields();
        }}
        okText="Update Status"
      >
        <Form form={statusForm} layout="vertical">
          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: "Please select status" }]}
          >
            <Select>
              <Option value="operational">Operational</Option>
              <Option value="maintenance">Under Maintenance</Option>
              <Option value="breakdown">Breakdown</Option>
            </Select>
          </Form.Item>
          <Form.Item name="reason" label="Reason (optional)">
            <Input.TextArea rows={3} placeholder="Reason for status change" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default MachineList;
